import { sampleBilinear } from '../sim/grid.ts';
import {
  clearCrossSection,
  crossSectionExaggeration,
  crossSectionLine,
} from '../state/avalanche.ts';
import { displaySnowDepth, snowDepthBase, terrain, uiMode } from '../state/store.ts';
import { formatNumber } from './format.ts';

const CHART_W = 480;
const CHART_H = 220;
const MARGIN = { top: 10, right: 14, bottom: 26, left: 46 };
const PLOT_W = CHART_W - MARGIN.left - MARGIN.right;
const PLOT_H = CHART_H - MARGIN.top - MARGIN.bottom;
const EXAGGERATIONS = [1, 2, 5, 10];

interface Sample {
  d: number;
  ground: number;
  before: number;
  now: number;
}

/**
 * Floating, dismissible chart of elevation along a user-drawn line: bare ground, the snow
 * surface before any avalanches ran, and the surface now — with debris (now > before) filled
 * blue and scoured snow (now < before) filled red (PLAN §11).
 *
 * The terrain is always drawn to fit; the ×N buttons exaggerate the *snow depths* (surfaces are
 * drawn at ground + depth·N). Metres of debris over hundreds of metres of relief are otherwise
 * a couple of pixels, and exaggerating the whole profile pushes the runout zone off the chart.
 */
export function CrossSectionPanel() {
  const line = crossSectionLine.value;
  const t = terrain.value;
  if (!line || !t || uiMode.value === 'simple') return null;

  const base = snowDepthBase.value;
  // The scrubbed snapshot's depth while dragging the timeline, else the exact evaluated depth.
  const now = displaySnowDepth.value;
  const step = t.plan.terrain.cellSize;
  const dx = line.b.x - line.a.x;
  const dy = line.b.y - line.a.y;
  const dist = Math.hypot(dx, dy);
  const n = Math.max(2, Math.round(dist / Math.max(1, step)));

  const exagg = crossSectionExaggeration.value;
  const samples: Sample[] = [];
  for (let i = 0; i <= n; i++) {
    const f = i / n;
    const x = line.a.x + dx * f;
    const y = line.a.y + dy * f;
    const ground = sampleBilinear(t.elevation, x, y);
    const beforeDepth = base ? Math.max(0, sampleBilinear(base, x, y)) : 0;
    const nowDepth = now ? Math.max(0, sampleBilinear(now, x, y)) : 0;
    samples.push({
      d: dist * f,
      ground,
      before: ground + beforeDepth * exagg,
      now: ground + nowDepth * exagg,
    });
  }

  let zMin = Infinity;
  let zMax = -Infinity;
  for (const s of samples) {
    zMin = Math.min(zMin, s.ground, s.before, s.now);
    zMax = Math.max(zMax, s.ground, s.before, s.now);
  }
  if (zMax - zMin < 1) zMax = zMin + 1;
  // A little headroom so nothing sits on the axes.
  const pad = 0.06 * (zMax - zMin);
  zMin -= pad;
  zMax += pad;

  const scale = PLOT_H / (zMax - zMin);
  const midZ = (zMin + zMax) / 2;
  const xAt = (d: number): number => MARGIN.left + (d / dist) * PLOT_W;
  const yAt = (z: number): number => MARGIN.top + PLOT_H / 2 - (z - midZ) * scale;

  const path = (key: 'ground' | 'before' | 'now'): string =>
    samples.map((s, i) => `${i === 0 ? 'M' : 'L'} ${xAt(s.d)} ${yAt(s[key])}`).join(' ');

  const distTicks = [0, dist / 2, dist];

  return (
    <div class="cross-section-panel">
      <div class="cross-section-header">
        <h3>Cross-section</h3>
        <div
          class="cross-section-exagg"
          title="Exaggerate snow depths (the terrain stays to scale)"
        >
          <span class="cross-section-exagg-label">Snow depth</span>
          {EXAGGERATIONS.map((e) => (
            <button
              key={e}
              type="button"
              class={'icon-button' + (exagg === e ? ' active' : '')}
              onClick={() => (crossSectionExaggeration.value = e)}
            >
              {e}×
            </button>
          ))}
        </div>
        <button type="button" class="icon-button" onClick={clearCrossSection} aria-label="Close">
          ×
        </button>
      </div>
      <svg width={CHART_W} height={CHART_H} class="cross-section-svg" role="img">
        <defs>
          <clipPath id="cross-section-plot">
            <rect x={MARGIN.left} y={MARGIN.top} width={PLOT_W} height={PLOT_H} />
          </clipPath>
        </defs>
        <g clip-path="url(#cross-section-plot)">
          {/* Deposit (blue) / scour (red) fill between "before" and "now", per segment so mixed
            sign along the line still reads correctly. */}
          {samples.slice(1).map((s, i) => {
            const a = samples[i]!;
            const delta = (s.now - s.before + (a.now - a.before)) / 2;
            if (Math.abs(delta) < 0.02) return null;
            const d = `M ${xAt(a.d)} ${yAt(a.before)} L ${xAt(s.d)} ${yAt(s.before)} L ${xAt(s.d)} ${yAt(s.now)} L ${xAt(a.d)} ${yAt(a.now)} Z`;
            return (
              <path
                key={i}
                d={d}
                class={delta > 0 ? 'cross-section-fill-deposit' : 'cross-section-fill-scour'}
              />
            );
          })}
          <path d={path('ground')} class="cross-section-line-ground" />
          <path d={path('before')} class="cross-section-line-before" />
          <path d={path('now')} class="cross-section-line-now" />
        </g>
        <line
          x1={MARGIN.left}
          y1={MARGIN.top}
          x2={MARGIN.left}
          y2={CHART_H - MARGIN.bottom}
          class="cross-section-axis"
        />
        <line
          x1={MARGIN.left}
          y1={CHART_H - MARGIN.bottom}
          x2={CHART_W - MARGIN.right}
          y2={CHART_H - MARGIN.bottom}
          class="cross-section-axis"
        />
        {distTicks.map((d) => (
          <text
            key={d}
            x={xAt(d)}
            y={CHART_H - MARGIN.bottom + 14}
            class="cross-section-axis-label"
            text-anchor="middle"
          >
            {formatNumber(d, 0)} m
          </text>
        ))}
        {[zMin, midZ, zMax].map((z) => (
          <text
            key={z}
            x={MARGIN.left - 6}
            y={yAt(z)}
            class="cross-section-axis-label"
            text-anchor="end"
          >
            {formatNumber(z, 0)}
          </text>
        ))}
      </svg>
      <div class="cross-section-legend">
        <span>
          <i class="cross-section-swatch cross-section-line-ground" /> Ground
        </span>
        <span>
          <i class="cross-section-swatch cross-section-line-before" /> Before
        </span>
        <span>
          <i class="cross-section-swatch cross-section-line-now" /> Now
        </span>
        <span>
          <i class="cross-section-swatch" style={{ background: '#3d7fd9' }} /> Debris
        </span>
        <span>
          <i class="cross-section-swatch" style={{ background: '#d9432f' }} /> Scoured
        </span>
      </div>
    </div>
  );
}
